<template>
  <div class="create-edit-lead">
    <form @submit.prevent="handleSubmit">
      <RadioCardGroup
        :label="$t('crm.create_lead.customer_type_label')"
        :options="customerTypeOptions"
        v-model="inputs.customer_type.binding"
        layout="grid"
        :columns="2"
        name="customer_type"
        @change="handleCustomerTypeChange"
      />

      <template v-if="isCompany">
        <h4 class="create-edit-lead__section-title">
          {{ $t('crm.create_lead.company_section') }}
        </h4>
        <TextInput
          :id="inputs.company_name.id"
          :label="$t('crm.create_lead.company_name')"
          v-model="inputs.company_name.binding"
          :error="inputs.company_name.error"
          :rules="inputs.company_name.rules"
          :placeholder="$t('crm.create_lead.company_name_placeholder')"
          @validate="onValidate('company_name', $event)"
        />
        <div class="create-edit-lead__row">
          <div class="create-edit-lead__field">
            <TextInput
              :id="inputs.kvk_number.id"
              :label="$t('crm.create_lead.kvk_number')"
              v-model="inputs.kvk_number.binding"
              :error="inputs.kvk_number.error"
              :rules="inputs.kvk_number.rules"
              :placeholder="$t('crm.create_lead.kvk_number_placeholder')"
              @validate="onValidate('kvk_number', $event)"
            />
            <p class="create-edit-lead__help">
              {{ $t('crm.create_lead.kvk_number_help') }}
            </p>
          </div>
          <div class="create-edit-lead__field">
            <TextInput
              :id="inputs.vat_number.id"
              :label="$t('crm.create_lead.vat_number')"
              v-model="inputs.vat_number.binding"
              :error="inputs.vat_number.error"
              :rules="inputs.vat_number.rules"
              :placeholder="$t('crm.create_lead.vat_number_placeholder')"
              @validate="onValidate('vat_number', $event)"
            />
            <p class="create-edit-lead__help">
              {{ $t('crm.create_lead.vat_number_help') }}
            </p>
          </div>
        </div>
        <SelectInput
          :id="inputs.legal_form.id"
          :label="$t('crm.create_lead.legal_form')"
          v-model="inputs.legal_form.binding"
          :options="legalFormOptions"
          :placeholder="$t('crm.create_lead.legal_form_placeholder')"
        />
        <ToggleInput
          :id="inputs.vat_reverse_charge.id"
          :label="$t('crm.create_lead.vat_reverse_charge')"
          v-model="inputs.vat_reverse_charge.binding"
        />
        <p class="create-edit-lead__help create-edit-lead__help--toggle">
          {{ $t('crm.create_lead.vat_reverse_charge_help') }}
        </p>
      </template>

      <h4 class="create-edit-lead__section-title">
        {{ $t('crm.create_lead.contact_info') }}
      </h4>
      <TextInput
        :id="inputs.name.id"
        :label="contactNameLabel"
        v-model="inputs.name.binding"
        :error="inputs.name.error"
        :rules="inputs.name.rules"
        :placeholder="$t('crm.create_lead.name_placeholder')"
        @validate="onValidate('name', $event)"
      />
      <TextInput
        :id="inputs.email.id"
        :label="$t('lead_details.email')"
        v-model="inputs.email.binding"
        :error="inputs.email.error"
        :rules="inputs.email.rules"
        :placeholder="$t('crm.create_lead.email_placeholder')"
        @validate="onValidate('email', $event)"
      />
      <TextInput
        :id="inputs.phone.id"
        :label="$t('lead_details.phone')"
        v-model="inputs.phone.binding"
        :error="inputs.phone.error"
        :rules="inputs.phone.rules"
        :placeholder="$t('crm.create_lead.phone_placeholder')"
        @validate="onValidate('phone', $event)"
      />

      <AddressInput
        :id="inputs.address.id"
        :label="$t('lead_details.address')"
        v-model="inputs.address.binding"
        :error="inputs.address.error"
        :rules="inputs.address.rules"
        @validate="onValidate('address', $event)"
      />

      <div class="create-edit-lead__billing">
        <button
          type="button"
          class="create-edit-lead__billing-toggle"
          :class="{ 'create-edit-lead__billing-toggle--open': showBilling }"
          @click="showBilling = !showBilling"
        >
          <BaseIcon name="chevron" size="14" class="create-edit-lead__billing-chevron" />
          <span>
            {{
              showBilling
                ? $t('crm.create_lead.hide_billing')
                : $t('crm.create_lead.show_billing')
            }}
          </span>
        </button>

        <div v-if="showBilling" class="create-edit-lead__billing-body">
          <p class="create-edit-lead__help">
            {{ $t('crm.create_lead.billing_section_help') }}
          </p>
          <TextInput
            :id="inputs.billing_email.id"
            :label="$t('crm.create_lead.billing_email')"
            v-model="inputs.billing_email.binding"
            :error="inputs.billing_email.error"
            :rules="inputs.billing_email.rules"
            :placeholder="$t('crm.create_lead.billing_email_placeholder')"
            @validate="onValidate('billing_email', $event)"
          />
          <AddressInput
            :id="inputs.billing_address.id"
            :label="$t('crm.create_lead.billing_address')"
            v-model="inputs.billing_address.binding"
          />
        </div>
      </div>

      <div class="form-actions">
        <LoadingSpinner v-if="isLoading" />
        <Btn
          type="button"
          :text="$t('common.cancel')"
          @click.prevent="handleCancel"
          variant="tertiary"
        />
        <Btn
          type="submit"
          :text="
            isEditMode ? $t('crm.create_lead.save_edit') : $t('crm.create_lead.save')
          "
          :loading="isLoading"
          variant="secondary"
        />
      </div>
    </form>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { crmService } from '@/services/crmService';
  import { useCrmStore } from '@/store/crmStore';
  import { useToastStore } from '@/store/toastStore';
  import { useValidation } from '@/composables/useValidation';
  import { useLeadAddressSync, addressSignature } from '@/composables/useLeadAddressSync';

  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import AddressInput from '@/components/inputs/AddressInput.vue';
  import RadioCardGroup from '@/components/inputs/RadioCardGroup.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const EMPTY_ADDRESS = {
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: '',
  };

  export default defineComponent({
    name: 'CreateEditLead',

    components: {
      LoadingSpinner,
      TextInput,
      SelectInput,
      ToggleInput,
      AddressInput,
      RadioCardGroup,
      Btn,
      BaseIcon,
    },

    props: {
      leadId: {
        type: String,
        default: null,
      },
      onClose: {
        type: Function,
        default: () => {},
      },
      onCreated: {
        type: Function,
        default: () => {},
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const crmStore = useCrmStore();
      const toastStore = useToastStore();

      const { promptAddressSync } = useLeadAddressSync();

      const isLoading = ref(false);
      const showBilling = ref(false);
      const isEditMode = computed(() => Boolean(props.leadId));
      const initialAddress = ref(null);

      const customerTypeOptions = computed(() => [
        {
          value: 'person',
          icon: 'user',
          title: $t('crm.create_lead.customer_type_person_title'),
          description: $t('crm.create_lead.customer_type_person_description'),
        },
        {
          value: 'company',
          icon: 'building',
          title: $t('crm.create_lead.customer_type_company_title'),
          description: $t('crm.create_lead.customer_type_company_description'),
        },
      ]);

      const legalFormKeys = [
        'bv',
        'nv',
        'vof',
        'eenmanszaak',
        'stichting',
        'vereniging',
        'cv',
        'maatschap',
        'other',
      ];

      const legalFormOptions = computed(() => [
        { value: '', label: $t('crm.create_lead.legal_form_placeholder') },
        ...legalFormKeys.map((k) => ({
          value: k,
          label: $t(`crm.create_lead.legal_form_options.${k}`),
        })),
      ]);

      const { inputs, validateForm, onValidate } = useValidation({
        customer_type: {
          id: 'customer_type',
          label: $t('crm.create_lead.customer_type_label'),
          binding: 'person',
          error: '',
          rules: ['required'],
        },
        company_name: {
          id: 'company_name',
          label: $t('crm.create_lead.company_name'),
          binding: '',
          error: '',
          rules: [],
        },
        kvk_number: {
          id: 'kvk_number',
          label: $t('crm.create_lead.kvk_number'),
          binding: '',
          error: '',
          rules: [],
        },
        vat_number: {
          id: 'vat_number',
          label: $t('crm.create_lead.vat_number'),
          binding: '',
          error: '',
          rules: [],
        },
        legal_form: {
          id: 'legal_form',
          label: $t('crm.create_lead.legal_form'),
          binding: '',
          error: '',
          rules: [],
        },
        vat_reverse_charge: {
          id: 'vat_reverse_charge',
          label: $t('crm.create_lead.vat_reverse_charge'),
          binding: false,
          error: '',
          rules: [],
        },
        name: {
          id: 'name',
          label: $t('lead_details.name'),
          binding: '',
          error: '',
          rules: ['required', 'maxLength:255'],
        },
        email: {
          id: 'email',
          label: $t('lead_details.email'),
          binding: '',
          error: '',
          rules: ['required', 'email'],
        },
        phone: {
          id: 'phone',
          label: $t('lead_details.phone'),
          binding: '',
          error: '',
          rules: ['maxLength:50'],
          required: false,
        },
        address: {
          id: 'address',
          label: $t('lead_details.address'),
          binding: { ...EMPTY_ADDRESS },
          error: '',
          rules: [],
        },
        billing_email: {
          id: 'billing_email',
          label: $t('crm.create_lead.billing_email'),
          binding: '',
          error: '',
          rules: [],
        },
        billing_address: {
          id: 'billing_address',
          label: $t('crm.create_lead.billing_address'),
          binding: { ...EMPTY_ADDRESS },
          error: '',
          rules: [],
        },
      });

      const isCompany = computed(() => inputs.value.customer_type.binding === 'company');

      const contactNameLabel = computed(() =>
        isCompany.value
          ? $t('lead_details.name') + ' (' + $t('lead_details.contact_details') + ')'
          : $t('lead_details.name')
      );

      const applyValidationRules = () => {
        if (isCompany.value) {
          inputs.value.company_name.rules = ['required', 'maxLength:255'];
          inputs.value.kvk_number.rules = ['required', 'kvkNl'];
          inputs.value.vat_number.rules = ['vatNl'];
        } else {
          inputs.value.company_name.rules = [];
          inputs.value.kvk_number.rules = [];
          inputs.value.vat_number.rules = [];
          inputs.value.company_name.error = '';
          inputs.value.kvk_number.error = '';
          inputs.value.vat_number.error = '';
        }
        inputs.value.billing_email.rules = inputs.value.billing_email.binding
          ? ['email']
          : [];
      };

      const handleCustomerTypeChange = () => {
        applyValidationRules();
      };

      const isAddressFilled = (a) =>
        a && Object.values(a).some((v) => v !== null && v !== undefined && String(v).trim() !== '');

      const buildPayload = () => {
        const payload = {
          customer_type: inputs.value.customer_type.binding,
          name: inputs.value.name.binding.trim(),
          email: inputs.value.email.binding.trim(),
          phone: inputs.value.phone.binding?.trim() || null,
          address: isAddressFilled(inputs.value.address.binding)
            ? inputs.value.address.binding
            : null,
        };

        if (isCompany.value) {
          payload.company_name = inputs.value.company_name.binding.trim();
          payload.kvk_number = inputs.value.kvk_number.binding.trim() || null;
          payload.vat_number =
            inputs.value.vat_number.binding.replace(/\s/g, '').toUpperCase() || null;
          payload.legal_form = inputs.value.legal_form.binding || null;
          payload.vat_reverse_charge = Boolean(inputs.value.vat_reverse_charge.binding);
        } else {
          payload.company_name = null;
          payload.kvk_number = null;
          payload.vat_number = null;
          payload.legal_form = null;
          payload.vat_reverse_charge = false;
        }

        const billingEmail = inputs.value.billing_email.binding?.trim() || '';
        const billingAddressFilled = isAddressFilled(inputs.value.billing_address.binding);
        payload.billing_email = billingEmail || null;
        payload.billing_address = billingAddressFilled
          ? inputs.value.billing_address.binding
          : null;

        return payload;
      };

      const prefillFromLead = (lead) => {
        if (!lead) return;
        inputs.value.customer_type.binding = lead.customer_type || 'person';
        inputs.value.name.binding = lead.name || '';
        inputs.value.email.binding = lead.email || '';
        inputs.value.phone.binding = lead.phone || '';
        inputs.value.address.binding = lead.address
          ? { ...EMPTY_ADDRESS, ...lead.address }
          : { ...EMPTY_ADDRESS };
        initialAddress.value = lead.address || null;
        inputs.value.company_name.binding = lead.company_name || '';
        inputs.value.kvk_number.binding = lead.kvk_number || '';
        inputs.value.vat_number.binding = lead.vat_number || '';
        inputs.value.legal_form.binding = lead.legal_form || '';
        inputs.value.vat_reverse_charge.binding = Boolean(lead.vat_reverse_charge);
        inputs.value.billing_email.binding = lead.billing_email || '';
        inputs.value.billing_address.binding = lead.billing_address
          ? { ...EMPTY_ADDRESS, ...lead.billing_address }
          : { ...EMPTY_ADDRESS };

        if (lead.billing_email || lead.billing_address) {
          showBilling.value = true;
        }
        applyValidationRules();
      };

      onMounted(async () => {
        applyValidationRules();
        if (!isEditMode.value) return;
        try {
          isLoading.value = true;
          const cached = crmStore.leads.find((l) => l.id === props.leadId);
          if (cached) {
            prefillFromLead(cached);
          }
          const fresh = await crmService.getLeadById(props.leadId);
          prefillFromLead(fresh);
        } catch (error) {
          console.error('Error loading lead for edit:', error);
          toastStore.addToast({
            message: $t('lead_details.lead_not_found'),
            type: 'error',
            duration: 4000,
          });
        } finally {
          isLoading.value = false;
        }
      });

      const handleSubmit = async () => {
        applyValidationRules();
        const allFieldsValid = await validateForm();
        if (!allFieldsValid) return;

        isLoading.value = true;
        try {
          const payload = buildPayload();
          if (isEditMode.value) {
            payload.id = props.leadId;
            const updated = await crmStore.updateLead(props.leadId, payload);
            toastStore.addToast({
              message: $t('crm.create_lead.update_success'),
              type: 'success',
              duration: 3000,
            });
            props.onCreated(updated);
            props.onClose();
            if (addressSignature(payload.address) !== addressSignature(initialAddress.value)) {
              await promptAddressSync(props.leadId, { previousAddress: initialAddress.value }, $t);
            }
            return;
          }

          const result = await crmService.createLead({ ...payload, status: 'new' });
          const statusCode = result.statusCode;
          const lead = { ...result };
          delete lead.statusCode;

          const existingIndex = crmStore.leads.findIndex((l) => l.id === lead.id);
          if (existingIndex === -1) {
            crmStore.leads.unshift(lead);
          } else {
            crmStore.leads[existingIndex] = lead;
          }

          if (statusCode === 201) {
            toastStore.addToast({
              message: $t('crm.create_lead.success'),
              type: 'success',
              duration: 3000,
            });
          } else if (statusCode === 200) {
            toastStore.addToast({
              message: $t('crm.create_lead.restored_from_archive'),
              type: 'success',
              duration: 3000,
            });
          } else if (statusCode === 409) {
            toastStore.addToast({
              message: $t('crm.create_lead.already_exists'),
              type: 'warning',
              duration: 5000,
            });
          }

          props.onCreated(lead);
          props.onClose();
        } catch (error) {
          console.error('Error saving lead:', error);
          const errorMessage =
            error.response?.data?.error ||
            (isEditMode.value
              ? $t('crm.create_lead.update_error')
              : $t('crm.create_lead.error'));
          toastStore.addToast({
            message: errorMessage,
            type: 'error',
            duration: 5000,
          });
        } finally {
          isLoading.value = false;
        }
      };

      const handleCancel = () => {
        props.onClose();
      };

      return {
        inputs,
        isLoading,
        isEditMode,
        isCompany,
        showBilling,
        customerTypeOptions,
        legalFormOptions,
        contactNameLabel,
        onValidate,
        handleSubmit,
        handleCancel,
        handleCustomerTypeChange,
      };
    },
  });
</script>

<style scoped lang="scss">
  .create-edit-lead {
    display: flex;
    flex-direction: column;

    form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__section-title {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-700);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin: 0.75rem 0 0;
    }

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }

    &__field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__help {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin: 0;
      line-height: 1.4;

      &--toggle {
        margin-top: -0.25rem;
      }
    }

    &__billing {
      border-top: 1px dashed var(--slate-200);
      padding-top: 1rem;
      margin-top: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__billing-toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      background: none;
      border: none;
      color: var(--brand-700);
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;
      padding: 0;
      align-self: flex-start;

      @include hover {
        color: var(--brand-800);
      }

      &--open {
        .create-edit-lead__billing-chevron {
          transform: rotate(90deg);
        }
      }
    }

    &__billing-chevron {
      transition: transform 0.15s ease;
    }

    &__billing-body {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      margin-top: 1rem;
    }
  }
</style>
